import React, { useCallback, useEffect, useRef, useState } from "react";
import AdminLayout from "../../Layout/AdminLayout";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import AdminChangePasswordModal from "../../Components/AdminChangePasswordModal/AdminChangePasswordModal";
import { Alert, AlertDescription } from "../../Components/ui/alert";
import { Button } from "../../Components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../Components/ui/card";
import { Checkbox } from "../../Components/ui/checkbox";
import { Input } from "../../Components/ui/input";
import { Label } from "../../Components/ui/label";
import { SelectField } from "../../Components/ui/select-field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../Components/ui/tabs";
import { Textarea } from "../../Components/ui/textarea";
import { cn } from "../../lib/utils";

const PREFS_KEY = "healbid_admin_prefs";

const MARKETPLACE_UNITS = ["minutes", "hours", "days"];

const DEFAULT_FOOTER_TERMS_ADMIN = [
  { label: "Patient terms & conditions", to: "/ConsentPage?role=patient" },
  {
    label: "Doctor terms & conditions",
    to: "mailto:support@healthcarehealbid.com?subject=Doctor%20terms%20request",
  },
  {
    label: "Hospital terms & conditions",
    to: "mailto:support@healthcarehealbid.com?subject=Hospital%20terms%20request",
  },
];

function emptyFooterDraft() {
  return {
    connectKicker: "Connect",
    contactTitle: "Get in touch",
    contactSubtitle: "We're here to help you navigate healthcare bidding.",
    supportEmail: "support@healthcarehealbid.com",
    supportPhoneDisplay: "+1 (844) 432-7849",
    supportPhoneTel: "+18444327849",
    mailingLabel: "Mailing & legal",
    mailingLinkLabel: "Request details",
    mailingLinkHref: "",
    brandTagline: "Transparent healthcare bidding for patients, doctors, and hospitals.",
    copyrightSiteName: "HealBid",
    contactLink: "/contact",
    cookieSettingsUrl: "/patient-profile/cookies",
    termsLinks: DEFAULT_FOOTER_TERMS_ADMIN.map((t) => ({ ...t })),
  };
}

function normalizeMarketplaceUnit(unit) {
  const u = String(unit || "").toLowerCase();
  return MARKETPLACE_UNITS.includes(u) ? u : "days";
}

/** Align with backend bid-response-window limits (10 min … 30 days). */
function clampMarketplaceExpiryForSave(valueRaw, unitRaw) {
  const unit = normalizeMarketplaceUnit(unitRaw);
  let v = Number(valueRaw);
  if (!Number.isFinite(v)) v = unit === "minutes" ? 10 : unit === "hours" ? 1 : 7;
  if (unit === "minutes") v = Math.round(Math.max(10, Math.min(30 * 24 * 60, v)));
  else if (unit === "hours") v = Math.round(Math.max(1, Math.min(30 * 24, v)));
  else v = Math.round(Math.max(1, Math.min(30, v)));
  return { requestExpiryValue: v, requestExpiryUnit: unit };
}

function marketplaceInputBounds(unit) {
  const u = normalizeMarketplaceUnit(unit);
  if (u === "minutes") return { min: 10, max: 30 * 24 * 60 };
  if (u === "hours") return { min: 1, max: 30 * 24 };
  return { min: 1, max: 30 };
}

function Field({ label, children, className }) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function CheckRow({ id, checked, onCheckedChange, title }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-transparent px-1 py-1 transition-colors hover:border-border/80 hover:bg-muted/30">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        className="mt-0.5"
      />
      <Label htmlFor={id} className="cursor-pointer font-normal leading-snug text-muted-foreground">
        {title}
      </Label>
    </div>
  );
}

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { compactTables: false };
    const parsed = JSON.parse(raw);
    return { compactTables: Boolean(parsed.compactTables) };
  } catch {
    return { compactTables: false };
  }
}

const emptyDraft = () => ({
  versions: { adminWeb: "", patientWeb: "", api: "" },
  support: { patientCarePhone: "" },
  social: {
    websiteUrl: "",
    facebookUrl: "",
    instagramUrl: "",
    twitterUrl: "",
    linkedinUrl: "",
    youtubeUrl: "",
    whatsappNumber: "",
  },
  seo: { defaultTitle: "", defaultDescription: "", ogImageUrl: "" },
  smtp: {
    host: "",
    port: 587,
    secure: false,
    user: "",
    fromEmail: "",
    fromName: "",
    replyTo: "",
  },
  legal: {
    termsHtml: "",
    privacyHtml: "",
    cookiePolicyHtml: "",
    refundPolicyHtml: "",
  },
  legalVersions: { terms: "1.0.0", privacy: "1.0.0" },
  compliance: { refundAutoApproveMaxInr: "" },
  marketplace: { requestExpiryValue: "7", requestExpiryUnit: "days" },
  dataCollection: {
    summary: "",
    categoriesCollected: "",
    retentionDays: "",
    dpoContactEmail: "",
    lawfulBasisNote: "",
  },
  features: {
    maintenanceMode: false,
    maintenanceMessage: "",
    userRegistrationEnabled: true,
    doctorSignupEnabled: true,
    hospitalSignupEnabled: true,
  },
  analytics: {
    googleAnalyticsMeasurementId: "",
    plausibleDomain: "",
    metaPixelId: "",
  },
  siteBanner: {
    enabled: false,
    text: "",
    pushLayout: true,
    alertEnabled: false,
    alertText: "",
    alertVariant: "warning",
  },
  footer: emptyFooterDraft(),
});

export default function Settings() {
  const [tab, setTab] = useState("account");

  const [admin, setAdmin] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [pwdOpen, setPwdOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);

  const [platformDraft, setPlatformDraft] = useState(emptyDraft);
  const [smtpHasPassword, setSmtpHasPassword] = useState(false);
  const [smtpPasswordInput, setSmtpPasswordInput] = useState("");
  const [clearSmtpPassword, setClearSmtpPassword] = useState(false);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformSaveBusy, setPlatformSaveBusy] = useState(false);
  const [platformMsg, setPlatformMsg] = useState({ type: "", text: "" });

  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastAudience, setBroadcastAudience] = useState("admin");
  const [broadcastBusy, setBroadcastBusy] = useState(false);

  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailBusy, setTestEmailBusy] = useState(false);

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditActionPrefix, setAuditActionPrefix] = useState("");
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");

  useEffect(() => {
    if (!pwdOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPwdOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pwdOpen]);

  useEffect(() => {
    const p = loadPrefs();
    try {
      document.body.classList.toggle("admin-compact-tables", Boolean(p.compactTables));
    } catch (_) {}
  }, []);

  useEffect(() => {
    setLoadError("");
    API.get(endpoints.ADMIN_ME)
      .then((res) => {
        const data = res?.data?.admin ?? res?.data;
        setAdmin(data || null);
      })
      .catch(() => {
        setAdmin(null);
        setLoadError("Could not load account details.");
      });
  }, []);

  const mapPlatformFromApi = useCallback((d) => {
    if (!d || typeof d !== "object") return emptyDraft();
    const base = emptyDraft();
    return {
      versions: { ...base.versions, ...(d.versions || {}) },
      support: { ...base.support, ...(d.support || {}) },
      social: { ...base.social, ...(d.social || {}) },
      seo: { ...base.seo, ...(d.seo || {}) },
      smtp: {
        ...base.smtp,
        ...(d.smtp || {}),
        port: Number(d.smtp?.port) || 587,
        secure: Boolean(d.smtp?.secure),
      },
      legal: { ...base.legal, ...(d.legal || {}) },
      legalVersions: {
        ...base.legalVersions,
        ...(d.legalVersions || {}),
      },
      compliance: {
        ...base.compliance,
        refundAutoApproveMaxInr:
          d.compliance?.refundAutoApproveMaxInr != null && d.compliance?.refundAutoApproveMaxInr !== ""
            ? String(d.compliance.refundAutoApproveMaxInr)
            : "",
      },
      marketplace: {
        ...base.marketplace,
        ...(d.marketplace || {}),
        requestExpiryUnit: normalizeMarketplaceUnit(d.marketplace?.requestExpiryUnit),
        requestExpiryValue: String(
          d.marketplace?.requestExpiryValue != null && d.marketplace?.requestExpiryValue !== ""
            ? d.marketplace.requestExpiryValue
            : d.marketplace?.requestExpiryDays != null && d.marketplace?.requestExpiryDays !== ""
              ? d.marketplace.requestExpiryDays
              : "7"
        ),
      },
      dataCollection: {
        ...base.dataCollection,
        ...(d.dataCollection || {}),
        retentionDays:
          d.dataCollection?.retentionDays != null && d.dataCollection?.retentionDays !== ""
            ? String(d.dataCollection.retentionDays)
            : "",
      },
      features: { ...base.features, ...(d.features || {}) },
      analytics: { ...base.analytics, ...(d.analytics || {}) },
      siteBanner: {
        ...base.siteBanner,
        ...(d.siteBanner || {}),
        enabled: Boolean(d.siteBanner?.enabled),
        pushLayout: d.siteBanner?.pushLayout !== false,
        text: String(d.siteBanner?.text || ""),
        alertEnabled: Boolean(d.siteBanner?.alertEnabled),
        alertText: String(d.siteBanner?.alertText || ""),
        alertVariant: ["info", "warning", "critical"].includes(d.siteBanner?.alertVariant)
          ? d.siteBanner.alertVariant
          : "warning",
      },
      footer: (() => {
        const bf = emptyFooterDraft();
        const apiF = d.footer && typeof d.footer === "object" ? d.footer : {};
        const merged = { ...bf };
        for (const k of Object.keys(bf)) {
          if (k === "termsLinks") continue;
          if (apiF[k] !== undefined && apiF[k] !== null) merged[k] = apiF[k];
        }
        merged.termsLinks = Array.isArray(apiF.termsLinks)
          ? apiF.termsLinks.map((t) => ({
              label: String(t?.label || "").trim(),
              to: String(t?.to || "").trim(),
            }))
          : bf.termsLinks.map((t) => ({ ...t }));
        return merged;
      })(),
    };
  }, []);

  const loadPlatform = useCallback(() => {
    setPlatformLoading(true);
    setPlatformMsg({ type: "", text: "" });
    API.get(endpoints.ADMIN_PLATFORM_SETTINGS)
      .then((res) => {
        const d = res?.data?.data ?? res?.data;
        setPlatformDraft(mapPlatformFromApi(d));
        setSmtpHasPassword(Boolean(d?.smtp?.hasPassword));
        setSmtpPasswordInput("");
        setClearSmtpPassword(false);
      })
      .catch(() => {
        setPlatformMsg({ type: "err", text: "Could not load platform settings." });
      })
      .finally(() => setPlatformLoading(false));
  }, [mapPlatformFromApi]);

  useEffect(() => {
    if (tab === "platform") loadPlatform();
  }, [tab, loadPlatform]);

  const auditFilterDebounceRef = useRef(null);

  useEffect(() => {
    if (tab !== "audit") return;

    if (auditFilterDebounceRef.current) clearTimeout(auditFilterDebounceRef.current);

    const run = () => {
      setAuditLoading(true);
      setAuditError("");
      API.get(endpoints.ADMIN_AUDIT_LOGS, {
        params: {
          page: auditPage,
          limit: 25,
          actionPrefix: auditActionPrefix.trim() || undefined,
        },
      })
        .then((res) => {
          const data = res?.data?.data ?? res?.data;
          setAuditLogs(data?.items || []);
          setAuditTotal(Number(data?.total) || 0);
        })
        .catch(() => {
          setAuditLogs([]);
          setAuditTotal(0);
          setAuditError("Could not load audit log.");
        })
        .finally(() => setAuditLoading(false));
    };

    auditFilterDebounceRef.current = setTimeout(run, auditActionPrefix ? 350 : 0);
    return () => {
      if (auditFilterDebounceRef.current) clearTimeout(auditFilterDebounceRef.current);
    };
  }, [tab, auditPage, auditActionPrefix]);

  const patchField = (section, key, value) => {
    setPlatformDraft((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const patchFooter = (key, value) => {
    setPlatformDraft((prev) => ({
      ...prev,
      footer: { ...(prev.footer || emptyFooterDraft()), [key]: value },
    }));
  };

  const patchFooterTermLink = (index, key, value) => {
    setPlatformDraft((prev) => {
      const list = [...(prev.footer?.termsLinks || [])];
      list[index] = { ...list[index], [key]: value };
      return {
        ...prev,
        footer: { ...(prev.footer || emptyFooterDraft()), termsLinks: list },
      };
    });
  };

  const addFooterTermLink = () => {
    setPlatformDraft((prev) => ({
      ...prev,
      footer: {
        ...(prev.footer || emptyFooterDraft()),
        termsLinks: [...(prev.footer?.termsLinks || []), { label: "", to: "" }],
      },
    }));
  };

  const removeFooterTermLink = (index) => {
    setPlatformDraft((prev) => {
      const list = [...(prev.footer?.termsLinks || [])].filter((_, i) => i !== index);
      return {
        ...prev,
        footer: { ...(prev.footer || emptyFooterDraft()), termsLinks: list },
      };
    });
  };

  const handleCompactToggle = (checked) => {
    const next = { ...prefs, compactTables: checked };
    setPrefs(next);
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(next));
    } catch (_) {}
    try {
      document.body.classList.toggle("admin-compact-tables", checked);
    } catch (_) {}
    window.dispatchEvent(new CustomEvent("healbid_admin_prefs_changed", { detail: next }));
  };

  const handleSavePlatform = async () => {
    setPlatformSaveBusy(true);
    setPlatformMsg({ type: "", text: "" });
    try {
      const smtp = { ...platformDraft.smtp };
      if (clearSmtpPassword) smtp.password = "***";
      else if (smtpPasswordInput.trim()) smtp.password = smtpPasswordInput.trim();

      const payload = {
        versions: platformDraft.versions,
        support: {
          patientCarePhone: String(platformDraft.support?.patientCarePhone || "").trim(),
        },
        social: platformDraft.social,
        seo: platformDraft.seo,
        smtp,
        legal: platformDraft.legal,
        legalVersions: platformDraft.legalVersions,
        compliance: {
          refundAutoApproveMaxInr:
            platformDraft.compliance.refundAutoApproveMaxInr === ""
              ? 0
              : Number(platformDraft.compliance.refundAutoApproveMaxInr) || 0,
        },
        marketplace: clampMarketplaceExpiryForSave(
          platformDraft.marketplace.requestExpiryValue,
          platformDraft.marketplace.requestExpiryUnit
        ),
        dataCollection: {
          ...platformDraft.dataCollection,
          retentionDays:
            platformDraft.dataCollection.retentionDays === ""
              ? null
              : Number(platformDraft.dataCollection.retentionDays) || null,
        },
        features: platformDraft.features,
        analytics: platformDraft.analytics,
        siteBanner: {
          enabled: Boolean(platformDraft.siteBanner?.enabled),
          pushLayout: platformDraft.siteBanner?.pushLayout !== false,
          text: String(platformDraft.siteBanner?.text || "").trim().slice(0, 600),
          alertEnabled: Boolean(platformDraft.siteBanner?.alertEnabled),
          alertText: String(platformDraft.siteBanner?.alertText || "").trim().slice(0, 400),
          alertVariant: ["info", "warning", "critical"].includes(platformDraft.siteBanner?.alertVariant)
            ? platformDraft.siteBanner.alertVariant
            : "warning",
        },
        footer: {
          connectKicker: String(platformDraft.footer?.connectKicker || "").trim(),
          contactTitle: String(platformDraft.footer?.contactTitle || "").trim(),
          contactSubtitle: String(platformDraft.footer?.contactSubtitle || "").trim(),
          supportEmail: String(platformDraft.footer?.supportEmail || "").trim(),
          supportPhoneDisplay: String(platformDraft.footer?.supportPhoneDisplay || "").trim(),
          supportPhoneTel: String(platformDraft.footer?.supportPhoneTel || "").trim(),
          mailingLabel: String(platformDraft.footer?.mailingLabel || "").trim(),
          mailingLinkLabel: String(platformDraft.footer?.mailingLinkLabel || "").trim(),
          mailingLinkHref: String(platformDraft.footer?.mailingLinkHref || "").trim(),
          brandTagline: String(platformDraft.footer?.brandTagline || "").trim(),
          copyrightSiteName: String(platformDraft.footer?.copyrightSiteName || "").trim(),
          contactLink: String(platformDraft.footer?.contactLink || "").trim(),
          cookieSettingsUrl: String(platformDraft.footer?.cookieSettingsUrl || "").trim(),
          termsLinks: (platformDraft.footer?.termsLinks || [])
            .map((t) => ({
              label: String(t.label || "").trim(),
              to: String(t.to || "").trim(),
            }))
            .filter((t) => t.label && t.to),
        },
      };

      const res = await API.put(endpoints.ADMIN_PLATFORM_SETTINGS, payload);
      const d = res?.data?.data ?? res?.data;
      setPlatformDraft(mapPlatformFromApi(d));
      setSmtpPasswordInput("");
      setClearSmtpPassword(false);
      setSmtpHasPassword(Boolean(d?.smtp?.hasPassword));
      setPlatformMsg({ type: "ok", text: res?.data?.message || "Platform settings saved." });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed.";
      setPlatformMsg({ type: "err", text: msg });
    } finally {
      setPlatformSaveBusy(false);
    }
  };

  const handleBroadcast = async () => {
    setBroadcastBusy(true);
    setPlatformMsg({ type: "", text: "" });
    try {
      await API.post(endpoints.ADMIN_PLATFORM_BROADCAST, {
        title: broadcastTitle.trim(),
        body: broadcastBody.trim(),
        audience: broadcastAudience,
      });
      setPlatformMsg({ type: "ok", text: "Broadcast sent." });
      setBroadcastTitle("");
      setBroadcastBody("");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Broadcast failed.";
      setPlatformMsg({ type: "err", text: msg });
    } finally {
      setBroadcastBusy(false);
    }
  };

  const handleTestEmail = async () => {
    setTestEmailBusy(true);
    setPlatformMsg({ type: "", text: "" });
    try {
      const res = await API.post(endpoints.ADMIN_PLATFORM_TEST_EMAIL, {
        to: testEmailTo.trim(),
      });
      setPlatformMsg({ type: "ok", text: res?.data?.message || "Test email sent." });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Test email failed.";
      setPlatformMsg({ type: "err", text: msg });
    } finally {
      setTestEmailBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mx-auto w-full max-w-3xl space-y-6 pb-28">
        <Tabs
          value={tab}
          onValueChange={(v) => {
            setTab(v);
            if (v === "audit") setAuditPage(1);
          }}
          className="w-full"
        >
          <TabsList className="grid h-auto min-h-11 w-full grid-cols-3 gap-1 p-1 sm:inline-flex sm:w-auto">
            <TabsTrigger value="account">Account</TabsTrigger>
            <TabsTrigger value="platform">Platform</TabsTrigger>
            <TabsTrigger value="audit">Audit log</TabsTrigger>
          </TabsList>

          <TabsContent value="account" className="mt-6 space-y-4 outline-none">
            {loadError && (
              <Alert variant="destructive">
                <AlertDescription>{loadError}</AlertDescription>
              </Alert>
            )}

            <Card className="overflow-hidden shadow-md">
              <CardHeader>
                <CardTitle>Account</CardTitle>
                <CardDescription>Signed-in admin profile from the server.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-0 pt-0">
                <dl className="divide-y divide-border">
                  <div className="grid grid-cols-1 gap-1 py-3 text-sm sm:grid-cols-[140px_1fr] sm:gap-4 sm:py-3.5">
                    <dt className="font-medium text-muted-foreground">Name</dt>
                    <dd className="text-foreground break-words">{admin?.name || "—"}</dd>
                  </div>
                  <div className="grid grid-cols-1 gap-1 py-3 text-sm sm:grid-cols-[140px_1fr] sm:gap-4 sm:py-3.5">
                    <dt className="font-medium text-muted-foreground">Mobile</dt>
                    <dd className="text-foreground break-words">{admin?.mobile || "—"}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Security</CardTitle>
                <CardDescription>
                  Change your password using OTP sent to your registered mobile.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button type="button" onClick={() => setPwdOpen(true)}>
                  Change password
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Display</CardTitle>
                <CardDescription>Optional preference stored on this device.</CardDescription>
              </CardHeader>
              <CardContent>
                <CheckRow
                  id="prefs-compact-tables"
                  checked={prefs.compactTables}
                  onCheckedChange={handleCompactToggle}
                  title="Compact tables in lists (where supported)"
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="platform" className="mt-6 space-y-4 outline-none">
            {platformLoading && (
              <p className="text-sm text-muted-foreground">Loading platform settings…</p>
            )}

            {platformMsg.text && (
              <Alert variant={platformMsg.type === "err" ? "destructive" : "success"}>
                <AlertDescription>{platformMsg.text}</AlertDescription>
              </Alert>
            )}

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Versions</CardTitle>
                <CardDescription>
                  Shown to clients and in public config (API exposes safe fields).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Admin web">
                  <Input
                    value={platformDraft.versions.adminWeb}
                    onChange={(e) => patchField("versions", "adminWeb", e.target.value)}
                    placeholder="1.0.0"
                  />
                </Field>
                <Field label="Patient / public web">
                  <Input
                    value={platformDraft.versions.patientWeb}
                    onChange={(e) => patchField("versions", "patientWeb", e.target.value)}
                    placeholder="1.0.0"
                  />
                </Field>
                <Field label="API">
                  <Input
                    value={platformDraft.versions.api}
                    onChange={(e) => patchField("versions", "api", e.target.value)}
                    placeholder="1.0.0"
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Patient support (Help assistant)</CardTitle>
                <CardDescription>
                  When patients ask for a phone number in the in-app Help assistant, this number is shown. Exposed via
                  the public platform API (no auth).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Field label="Care team phone (E.164 or local digits)">
                  <Input
                    value={platformDraft.support.patientCarePhone}
                    onChange={(e) => patchField("support", "patientCarePhone", e.target.value)}
                    placeholder="+91 …"
                    autoComplete="off"
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Social &amp; website</CardTitle>
                <CardDescription>Used in footers and marketing.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    ["websiteUrl", "Website URL"],
                    ["facebookUrl", "Facebook"],
                    ["instagramUrl", "Instagram"],
                    ["twitterUrl", "X / Twitter"],
                    ["linkedinUrl", "LinkedIn"],
                    ["youtubeUrl", "YouTube"],
                  ].map(([key, lbl]) => (
                    <Field key={key} label={lbl}>
                      <Input
                        value={platformDraft.social[key]}
                        onChange={(e) => patchField("social", key, e.target.value)}
                      />
                    </Field>
                  ))}
                </div>
                <Field label="WhatsApp (number or link)">
                  <Input
                    value={platformDraft.social.whatsappNumber}
                    onChange={(e) => patchField("social", "whatsappNumber", e.target.value)}
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Patient website footer</CardTitle>
                <CardDescription>
                  “Get in touch”, contact details, legal links, and brand line shown in the marketing footer. Icons for
                  “Follow us” use the URLs from Social &amp; website above when set.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Eyebrow (e.g. Connect)">
                    <Input
                      value={platformDraft.footer.connectKicker}
                      onChange={(e) => patchFooter("connectKicker", e.target.value)}
                    />
                  </Field>
                  <Field label="Section title">
                    <Input
                      value={platformDraft.footer.contactTitle}
                      onChange={(e) => patchFooter("contactTitle", e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Intro sentence">
                  <Textarea
                    rows={2}
                    value={platformDraft.footer.contactSubtitle}
                    onChange={(e) => patchFooter("contactSubtitle", e.target.value)}
                  />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Public support email">
                    <Input
                      type="email"
                      value={platformDraft.footer.supportEmail}
                      onChange={(e) => patchFooter("supportEmail", e.target.value)}
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Phone (display)">
                    <Input
                      value={platformDraft.footer.supportPhoneDisplay}
                      onChange={(e) => patchFooter("supportPhoneDisplay", e.target.value)}
                      placeholder="+1 …"
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Phone (tel link, optional E.164)">
                    <Input
                      value={platformDraft.footer.supportPhoneTel}
                      onChange={(e) => patchFooter("supportPhoneTel", e.target.value)}
                      placeholder="+18444327849"
                      autoComplete="off"
                    />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Address / legal column label">
                    <Input value={platformDraft.footer.mailingLabel} onChange={(e) => patchFooter("mailingLabel", e.target.value)} />
                  </Field>
                  <Field label="Address / legal link text">
                    <Input
                      value={platformDraft.footer.mailingLinkLabel}
                      onChange={(e) => patchFooter("mailingLinkLabel", e.target.value)}
                    />
                  </Field>
                  <Field label="Address / legal link (mailto: or URL)" className="sm:col-span-2">
                    <Input
                      value={platformDraft.footer.mailingLinkHref}
                      onChange={(e) => patchFooter("mailingLinkHref", e.target.value)}
                      placeholder="mailto:… or https://…"
                      autoComplete="off"
                    />
                  </Field>
                </div>
                <Field label="Brand tagline (under logo)">
                  <Textarea
                    rows={2}
                    value={platformDraft.footer.brandTagline}
                    onChange={(e) => patchFooter("brandTagline", e.target.value)}
                  />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Copyright name">
                    <Input
                      value={platformDraft.footer.copyrightSiteName}
                      onChange={(e) => patchFooter("copyrightSiteName", e.target.value)}
                    />
                  </Field>
                  <Field label="Footer “Contact” path">
                    <Input value={platformDraft.footer.contactLink} onChange={(e) => patchFooter("contactLink", e.target.value)} placeholder="/contact" />
                  </Field>
                  <Field label="Cookie settings path">
                    <Input
                      value={platformDraft.footer.cookieSettingsUrl}
                      onChange={(e) => patchFooter("cookieSettingsUrl", e.target.value)}
                      placeholder="/patient-profile/cookies"
                    />
                  </Field>
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-end justify-between gap-2">
                    <Label className="text-base">Terms &amp; conditions links</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addFooterTermLink}>
                      Add link
                    </Button>
                  </div>
                  <div className="space-y-3 rounded-lg border border-border p-3">
                    {(platformDraft.footer?.termsLinks || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No links — footer will fall back to defaults on the website.</p>
                    ) : null}
                    {(platformDraft.footer?.termsLinks || []).map((row, i) => (
                      <div key={i} className="flex flex-col gap-2 rounded-md border border-border/60 bg-muted/20 p-2 sm:flex-row sm:items-start">
                        <Field label="Label" className="min-w-0 flex-1">
                          <Input
                            value={row.label}
                            onChange={(e) => patchFooterTermLink(i, "label", e.target.value)}
                            placeholder="Patient terms…"
                          />
                        </Field>
                        <Field label="Path or URL" className="min-w-0 flex-[1.2]">
                          <Input
                            value={row.to}
                            onChange={(e) => patchFooterTermLink(i, "to", e.target.value)}
                            placeholder="/ConsentPage… or mailto:…"
                          />
                        </Field>
                        <div className="flex shrink-0 items-end pb-2 sm:pl-2">
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeFooterTermLink(i)}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>SEO defaults</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Default title">
                  <Input
                    value={platformDraft.seo.defaultTitle}
                    onChange={(e) => patchField("seo", "defaultTitle", e.target.value)}
                  />
                </Field>
                <Field label="Default description">
                  <Textarea
                    rows={3}
                    value={platformDraft.seo.defaultDescription}
                    onChange={(e) => patchField("seo", "defaultDescription", e.target.value)}
                  />
                </Field>
                <Field label="OG image URL">
                  <Input
                    value={platformDraft.seo.ogImageUrl}
                    onChange={(e) => patchField("seo", "ogImageUrl", e.target.value)}
                    placeholder="https://..."
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Email (SMTP)</CardTitle>
                <CardDescription>
                  Password is never shown after save. Leave blank to keep the stored password.
                  {smtpHasPassword ? " A password is on file." : " No password stored yet."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Host">
                    <Input
                      value={platformDraft.smtp.host}
                      onChange={(e) => patchField("smtp", "host", e.target.value)}
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Port">
                    <Input
                      type="number"
                      value={platformDraft.smtp.port}
                      onChange={(e) => patchField("smtp", "port", Number(e.target.value) || 587)}
                    />
                  </Field>
                </div>
                <CheckRow
                  id="smtp-secure"
                  checked={platformDraft.smtp.secure}
                  onCheckedChange={(v) => patchField("smtp", "secure", v)}
                  title="TLS / secure (port 465)"
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="SMTP user">
                    <Input
                      value={platformDraft.smtp.user}
                      onChange={(e) => patchField("smtp", "user", e.target.value)}
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="New password">
                    <Input
                      type="password"
                      value={smtpPasswordInput}
                      onChange={(e) => setSmtpPasswordInput(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                  </Field>
                  <Field label="From email">
                    <Input
                      value={platformDraft.smtp.fromEmail}
                      onChange={(e) => patchField("smtp", "fromEmail", e.target.value)}
                    />
                  </Field>
                  <Field label="From name">
                    <Input
                      value={platformDraft.smtp.fromName}
                      onChange={(e) => patchField("smtp", "fromName", e.target.value)}
                    />
                  </Field>
                  <Field label="Reply-To" className="sm:col-span-2">
                    <Input
                      value={platformDraft.smtp.replyTo}
                      onChange={(e) => patchField("smtp", "replyTo", e.target.value)}
                    />
                  </Field>
                </div>
                <CheckRow
                  id="smtp-clear-pwd"
                  checked={clearSmtpPassword}
                  onCheckedChange={setClearSmtpPassword}
                  title="Remove stored SMTP password on save"
                />
                <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-end">
                  <Field label="Send test to" className="min-w-0 flex-1">
                    <Input
                      type="email"
                      value={testEmailTo}
                      onChange={(e) => setTestEmailTo(e.target.value)}
                      placeholder="you@company.com"
                    />
                  </Field>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={testEmailBusy || !testEmailTo.trim()}
                    onClick={handleTestEmail}
                    className="shrink-0"
                  >
                    {testEmailBusy ? "Sending…" : "Send test email"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Legal &amp; policy pages</CardTitle>
                <CardDescription>
                  HTML for in-app pages; sanitize before rendering on the client.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {["termsHtml", "privacyHtml", "cookiePolicyHtml", "refundPolicyHtml"].map((key) => (
                  <Field
                    key={key}
                    label={
                      key === "termsHtml"
                        ? "Terms of service"
                        : key === "privacyHtml"
                          ? "Privacy policy"
                          : key === "cookiePolicyHtml"
                            ? "Cookie policy"
                            : "Refund policy"
                    }
                  >
                    <Textarea
                      rows={5}
                      value={platformDraft.legal[key]}
                      onChange={(e) => patchField("legal", key, e.target.value)}
                    />
                  </Field>
                ))}
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Legal versions &amp; refund automation</CardTitle>
                <CardDescription>
                  Bump a version when you change the corresponding HTML above; patients must re-accept. Refund
                  auto-approve applies when a dispute is resolved with a refund at or below this INR amount (0 = never
                  auto-approve).
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field label="Terms version">
                  <Input
                    type="text"
                    value={platformDraft.legalVersions.terms}
                    onChange={(e) =>
                      setPlatformDraft((p) => ({
                        ...p,
                        legalVersions: { ...p.legalVersions, terms: e.target.value },
                      }))
                    }
                    placeholder="e.g. 1.1.0"
                  />
                </Field>
                <Field label="Privacy version">
                  <Input
                    type="text"
                    value={platformDraft.legalVersions.privacy}
                    onChange={(e) =>
                      setPlatformDraft((p) => ({
                        ...p,
                        legalVersions: { ...p.legalVersions, privacy: e.target.value },
                      }))
                    }
                    placeholder="e.g. 1.1.0"
                  />
                </Field>
                <Field label="Auto-approve refunds up to (INR)" className="sm:col-span-2 lg:col-span-1">
                  <Input
                    type="number"
                    min={0}
                    value={platformDraft.compliance.refundAutoApproveMaxInr}
                    onChange={(e) =>
                      setPlatformDraft((p) => ({
                        ...p,
                        compliance: { ...p.compliance, refundAutoApproveMaxInr: e.target.value },
                      }))
                    }
                    placeholder="0"
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Marketplace timelines</CardTitle>
                <CardDescription>
                  Time allowed for doctors and hospitals to respond to each patient bid after it is created. Minimum 10
                  minutes; maximum 30 days.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Field label="Bid response window" className="sm:col-span-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="number"
                      {...marketplaceInputBounds(platformDraft.marketplace.requestExpiryUnit)}
                      value={platformDraft.marketplace.requestExpiryValue}
                      onChange={(e) =>
                        setPlatformDraft((p) => ({
                          ...p,
                          marketplace: { ...p.marketplace, requestExpiryValue: e.target.value },
                        }))
                      }
                      className="max-w-[140px]"
                      aria-label="Bid response window amount"
                    />
                    <SelectField
                      className="max-w-[180px]"
                      value={normalizeMarketplaceUnit(platformDraft.marketplace.requestExpiryUnit)}
                      onChange={(e) =>
                        setPlatformDraft((p) => ({
                          ...p,
                          marketplace: {
                            ...p.marketplace,
                            requestExpiryUnit: e.target.value,
                          },
                        }))
                      }
                      aria-label="Bid response window unit"
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </SelectField>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Examples: <strong>10</strong> minutes, <strong>12</strong> hours, <strong>5</strong> or{" "}
                    <strong>10</strong> days.
                  </p>
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Data collection &amp; privacy</CardTitle>
                <CardDescription>
                  Disclosure text for regulators and users (also exposed in public API subset).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Summary">
                  <Textarea
                    rows={3}
                    value={platformDraft.dataCollection.summary}
                    onChange={(e) => patchField("dataCollection", "summary", e.target.value)}
                  />
                </Field>
                <Field label="Categories of data collected">
                  <Textarea
                    rows={3}
                    value={platformDraft.dataCollection.categoriesCollected}
                    onChange={(e) => patchField("dataCollection", "categoriesCollected", e.target.value)}
                  />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Retention (days)">
                    <Input
                      type="number"
                      min={0}
                      value={platformDraft.dataCollection.retentionDays}
                      onChange={(e) => patchField("dataCollection", "retentionDays", e.target.value)}
                      placeholder="e.g. 365"
                    />
                  </Field>
                  <Field label="DPO / privacy contact email">
                    <Input
                      type="email"
                      value={platformDraft.dataCollection.dpoContactEmail}
                      onChange={(e) => patchField("dataCollection", "dpoContactEmail", e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Lawful basis (notes)">
                  <Textarea
                    rows={3}
                    value={platformDraft.dataCollection.lawfulBasisNote}
                    onChange={(e) => patchField("dataCollection", "lawfulBasisNote", e.target.value)}
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Website announcement strip</CardTitle>
                <CardDescription>
                  Optional alert (static, dismissible) and scrolling strip. On the patient app they appear in the top
                  bar beside the HealBid logo (wide screens); on doctor / hospital dashboards they use the layout
                  options below. Leave each message empty to hide that piece even when its toggle is on.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CheckRow
                  id="banner-enabled"
                  checked={platformDraft.siteBanner.enabled}
                  onCheckedChange={(v) => patchField("siteBanner", "enabled", v)}
                  title="Show announcement strip"
                />
                <CheckRow
                  id="banner-push"
                  checked={platformDraft.siteBanner.pushLayout}
                  onCheckedChange={(v) => patchField("siteBanner", "pushLayout", v)}
                  title="Push layout (reserve space; strip scrolls away with the page)"
                />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  When push layout is off, the strip stays fixed to the top of the screen and a spacer keeps content
                  from sitting underneath it.
                </p>
                <Field label="Announcement text">
                  <Textarea
                    rows={2}
                    maxLength={600}
                    placeholder="e.g. Holiday hours: support is limited Dec 24–26."
                    value={platformDraft.siteBanner.text}
                    onChange={(e) => patchField("siteBanner", "text", e.target.value)}
                  />
                </Field>
                <CheckRow
                  id="banner-alert"
                  checked={platformDraft.siteBanner.alertEnabled}
                  onCheckedChange={(v) => patchField("siteBanner", "alertEnabled", v)}
                  title="Show alert banner (above the strip; dismissible by users)"
                />
                <Field label="Alert tone">
                  <SelectField
                    value={platformDraft.siteBanner.alertVariant}
                    onChange={(e) => patchField("siteBanner", "alertVariant", e.target.value)}
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                  </SelectField>
                </Field>
                <Field label="Alert message">
                  <Textarea
                    rows={2}
                    maxLength={400}
                    placeholder="e.g. Payments are paused for maintenance until 6pm IST."
                    value={platformDraft.siteBanner.alertText}
                    onChange={(e) => patchField("siteBanner", "alertText", e.target.value)}
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Feature flags &amp; maintenance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <CheckRow
                  id="feat-maintenance"
                  checked={platformDraft.features.maintenanceMode}
                  onCheckedChange={(v) => patchField("features", "maintenanceMode", v)}
                  title="Maintenance mode (combine with public client checks)"
                />
                <Field label="Maintenance message">
                  <Textarea
                    rows={2}
                    value={platformDraft.features.maintenanceMessage}
                    onChange={(e) => patchField("features", "maintenanceMessage", e.target.value)}
                  />
                </Field>
                <CheckRow
                  id="feat-user-reg"
                  checked={platformDraft.features.userRegistrationEnabled}
                  onCheckedChange={(v) => patchField("features", "userRegistrationEnabled", v)}
                  title="Patient / user registration enabled"
                />
                <CheckRow
                  id="feat-doc-signup"
                  checked={platformDraft.features.doctorSignupEnabled}
                  onCheckedChange={(v) => patchField("features", "doctorSignupEnabled", v)}
                  title="Doctor signup enabled"
                />
                <CheckRow
                  id="feat-hosp-signup"
                  checked={platformDraft.features.hospitalSignupEnabled}
                  onCheckedChange={(v) => patchField("features", "hospitalSignupEnabled", v)}
                  title="Hospital signup enabled"
                />
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Analytics &amp; tags</CardTitle>
                <CardDescription>IDs only; load scripts in the website/app shell.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="Google Analytics (G-…)">
                  <Input
                    value={platformDraft.analytics.googleAnalyticsMeasurementId}
                    onChange={(e) =>
                      patchField("analytics", "googleAnalyticsMeasurementId", e.target.value)
                    }
                  />
                </Field>
                <Field label="Plausible domain">
                  <Input
                    value={platformDraft.analytics.plausibleDomain}
                    onChange={(e) => patchField("analytics", "plausibleDomain", e.target.value)}
                  />
                </Field>
                <Field label="Meta Pixel ID" className="sm:col-span-2">
                  <Input
                    value={platformDraft.analytics.metaPixelId}
                    onChange={(e) => patchField("analytics", "metaPixelId", e.target.value)}
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Realtime broadcast</CardTitle>
                <CardDescription>
                  Pushes a Socket.IO <code className="rounded bg-muted px-1 py-0.5 text-xs">notification:new</code>{" "}
                  event. &quot;Admins&quot; targets the admin room; &quot;Everyone&quot; emits to all connected clients
                  (use sparingly).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Title">
                  <Input value={broadcastTitle} onChange={(e) => setBroadcastTitle(e.target.value)} />
                </Field>
                <Field label="Body">
                  <Textarea rows={3} value={broadcastBody} onChange={(e) => setBroadcastBody(e.target.value)} />
                </Field>
                <Field label="Audience">
                  <SelectField value={broadcastAudience} onChange={(e) => setBroadcastAudience(e.target.value)}>
                    <option value="admin">Admins</option>
                    <option value="all">Everyone connected</option>
                  </SelectField>
                </Field>
                <Button
                  type="button"
                  variant="outline"
                  disabled={broadcastBusy || !broadcastTitle.trim() || !broadcastBody.trim()}
                  onClick={handleBroadcast}
                >
                  {broadcastBusy ? "Sending…" : "Send broadcast"}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-dashed bg-muted/40 shadow-none">
              <CardHeader>
                <CardTitle>Production checklist (next)</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground leading-relaxed">
                  <li>
                    Encrypt SMTP secret at rest; prefer env-based{" "}
                    <code className="rounded bg-background px-1 py-0.5 text-xs ring-1 ring-border">SMTP_PASS</code> in
                    production.
                  </li>
                  <li>
                    Rate limits are applied to auth and platform broadcast/test-email; audit log records sensitive
                    admin actions.
                  </li>
                  <li>
                    Wire public web app to{" "}
                    <code className="rounded bg-background px-1 py-0.5 text-xs ring-1 ring-border">
                      GET /app/platform-settings/public
                    </code>{" "}
                    for footer, legal, SEO, flags.
                  </li>
                  <li>
                    Consent logs, data export / deletion workflows, and DPIA documentation for regulated markets.
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="sticky bottom-0 z-10 -mx-1 border-t border-border/80 bg-gradient-to-t from-background via-background/95 to-transparent px-1 pb-2 pt-4 backdrop-blur-[2px]">
              <Button
                type="button"
                className="w-full sm:w-auto"
                disabled={platformSaveBusy || platformLoading}
                onClick={handleSavePlatform}
              >
                {platformSaveBusy ? "Saving…" : "Save platform settings"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-6 outline-none">
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle>Admin audit log</CardTitle>
                <CardDescription>
                  Immutable record of sensitive admin actions (approvals, removals, platform changes). Does not store
                  full policy HTML or secrets.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {auditError && (
                  <Alert variant="destructive">
                    <AlertDescription>{auditError}</AlertDescription>
                  </Alert>
                )}
                <Field label="Filter by action prefix">
                  <Input
                    value={auditActionPrefix}
                    onChange={(e) => {
                      setAuditActionPrefix(e.target.value);
                      setAuditPage(1);
                    }}
                    placeholder="e.g. doctor_ or platform_"
                  />
                </Field>
                <div className="overflow-x-auto rounded-lg border border-border bg-muted/30">
                  {auditLoading ? (
                    <p className="p-6 text-sm text-muted-foreground">Loading…</p>
                  ) : (
                    <table className="w-full min-w-[640px] border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/80">
                          <th className="whitespace-nowrap px-3 py-3 text-left font-semibold text-foreground">
                            Time
                          </th>
                          <th className="whitespace-nowrap px-3 py-3 text-left font-semibold text-foreground">
                            Action
                          </th>
                          <th className="whitespace-nowrap px-3 py-3 text-left font-semibold text-foreground">
                            Entity
                          </th>
                          <th className="whitespace-nowrap px-3 py-3 text-left font-semibold text-foreground">
                            Actor
                          </th>
                          <th className="whitespace-nowrap px-3 py-3 text-left font-semibold text-foreground">IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-3 py-10 text-center text-muted-foreground">
                              No entries yet.
                            </td>
                          </tr>
                        ) : (
                          auditLogs.map((row) => (
                            <tr key={row._id} className="border-b border-border last:border-b-0">
                              <td className="px-3 py-2.5 align-top font-mono text-xs text-foreground">
                                {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                              </td>
                              <td className="px-3 py-2.5 align-top">{row.action}</td>
                              <td className="px-3 py-2.5 align-top font-mono text-xs break-all">
                                {(row.entityType || "—") + (row.entityId ? ` · ${row.entityId}` : "")}
                              </td>
                              <td className="px-3 py-2.5 align-top">{row.actor?.name || "—"}</td>
                              <td className="px-3 py-2.5 align-top font-mono text-xs break-all">{row.ip || "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
                {auditTotal > 0 && (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={auditPage <= 1 || auditLoading}
                      onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className="text-center text-sm text-muted-foreground">
                      Page {auditPage} of {Math.max(1, Math.ceil(auditTotal / 25))} · {auditTotal} total
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={auditLoading || auditPage * 25 >= auditTotal}
                      onClick={() => setAuditPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <AdminChangePasswordModal isOpen={pwdOpen} onClose={() => setPwdOpen(false)} />
    </AdminLayout>
  );
}
